import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CircleAlert,
  Code2,
  LockKeyhole,
  Play,
  RotateCcw,
  Search,
  SearchX,
} from 'lucide-react';
import EmptyState from '../../components/site/EmptyState';
import PageHeader from '../../components/site/PageHeader';
import SEO from '../../components/SEO';
import { seoConfig } from '../../configs/seoConfig';
import { codePath } from '../../services/githubService';
import { useAppDispatch, useAppState } from '../../state/AppStateProvider';
import { formatDate } from '../../utils/format';
import { fetchGithubCodes } from '../githubCode/states/Actions';
import './GithubProjectsPage.css';

const isUrl = value => /^https?:\/\//i.test(value || '');

const tagsOf = project =>
  Array.isArray(project?.tags)
    ? [
        ...new Set(
          project.tags
            .filter(tag => typeof tag === 'string')
            .map(tag => tag.trim())
            .filter(Boolean)
        ),
      ]
    : [];

const matches = (project, query) => {
  if (!query) return true;
  const haystack = [project.title, project.description, project.author, ...tagsOf(project)]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(query);
};

// The whole card opens the project's own page (/code/:slug), where the code link lives.
const ProjectCard = ({ project }) => {
  const tags = tagsOf(project);
  const hasVideo = isUrl(project.videoLink);
  const meta = [
    project.author?.trim(),
    project.createdAt ? formatDate(project.createdAt) : null,
  ].filter(value => value && value !== '—');

  return (
    <article className="pk-card gh-card">
      <div className="gh-card__head">
        <span className="gh-card__icon" aria-hidden="true">
          <Code2 />
        </span>
        <h2 className="gh-card__title">
          <Link className="gh-card__link" to={codePath(project)}>
            {project.title || 'Untitled project'}
          </Link>
        </h2>
      </div>

      {project.description && <p className="gh-card__desc">{project.description}</p>}

      {tags.length > 0 && (
        <ul className="gh-card__tags" aria-label="Tags">
          {tags.map(tag => (
            <li key={tag} className="pk-badge">
              {tag}
            </li>
          ))}
        </ul>
      )}

      {meta.length > 0 && <p className="gh-card__meta">{meta.join(' · ')}</p>}

      <div className="gh-card__foot">
        <span className="gh-card__badges">
          {hasVideo && (
            <span className="pk-badge">
              <Play aria-hidden="true" />
              Video
            </span>
          )}
          {project.requireSignIn && (
            <span className="pk-badge" data-tone="info">
              <LockKeyhole aria-hidden="true" />
              Free account
            </span>
          )}
        </span>
        <span className="gh-card__cta" aria-hidden="true">
          Get the code
          <ArrowRight />
        </span>
      </div>
    </article>
  );
};

const GithubProjectsPage = () => {
  const { githubState } = useAppState();
  const dispatch = useAppDispatch();
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [tag, setTag] = useState('');

  const load = useCallback(async () => {
    setStatus('loading');
    setError('');
    const result = await fetchGithubCodes({ dispatch });
    if (result.success) {
      setStatus('ready');
    } else {
      setError(result.error || "We couldn't load the free code.");
      setStatus('error');
    }
  }, [dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  const projects = useMemo(
    () => (Array.isArray(githubState?.githubCode) ? githubState.githubCode : []),
    [githubState?.githubCode]
  );

  const allTags = useMemo(() => {
    const unique = new Set(projects.flatMap(tagsOf));
    return [...unique].sort((a, b) => a.localeCompare(b));
  }, [projects]);

  const needle = query.trim().toLowerCase();
  const visible = projects.filter(
    project =>
      matches(project, needle) && (!tag || tagsOf(project).includes(tag))
  );

  const clearFilters = () => {
    setQuery('');
    setTag('');
  };

  let content;
  if (status === 'loading') {
    content = (
      <div className="pk-grid pk-grid--3" aria-busy="true" aria-label="Loading projects">
        {[0, 1, 2].map(i => (
          <span key={i} className="pk-skel gh-skel" />
        ))}
      </div>
    );
  } else if (status === 'error') {
    content = (
      <EmptyState
        role="alert"
        icon={<CircleAlert />}
        title="Couldn't load the free code"
        text={error}
        actions={
          <button
            type="button"
            className="pk-btn pk-btn--secondary pk-btn--sm"
            onClick={load}
          >
            <RotateCcw aria-hidden="true" />
            Retry
          </button>
        }
      />
    );
  } else if (projects.length === 0) {
    content = (
      <EmptyState
        icon={<Code2 />}
        title="No free code yet"
        text="Code from our YouTube videos will be listed here when we share it."
        actions={
          <Link className="pk-btn pk-btn--primary pk-btn--sm" to="/products">
            Browse projects
          </Link>
        }
      />
    );
  } else if (visible.length === 0) {
    content = (
      <EmptyState
        icon={<SearchX />}
        title="No projects match"
        text="Try a different search or tag."
        actions={
          <button
            type="button"
            className="pk-btn pk-btn--secondary pk-btn--sm"
            onClick={clearFilters}
          >
            Clear filters
          </button>
        }
      />
    );
  } else {
    content = (
      <div className="pk-grid pk-grid--3">
        {visible.map(project => (
          <ProjectCard key={project.id || project.link} project={project} />
        ))}
      </div>
    );
  }

  return (
    <main className="pk-page">
      <SEO {...seoConfig.pages.githubProjects} />
      <div className="pk-container">
        <PageHeader
          title="Free source code"
          subtitle="Code from our YouTube videos, free to download. Open a project to watch the video and get the code."
          crumbs={[{ label: 'Home', to: '/' }, { label: 'Free code' }]}
        />

        {status === 'ready' && projects.length > 0 && (
          <div className="gh-toolbar">
            <div className="pk-input-icon gh-search">
              <Search aria-hidden="true" />
              <label className="gh-sr" htmlFor="gh-search">
                Search free code
              </label>
              <input
                id="gh-search"
                className="pk-input"
                type="search"
                placeholder="Search by name, tag or author"
                value={query}
                onChange={e => setQuery(e.target.value)}
                autoComplete="off"
              />
            </div>
            {allTags.length > 1 && (
              <div className="gh-tags" role="group" aria-label="Filter by tag">
                <button
                  type="button"
                  className="pk-chip"
                  aria-pressed={tag === ''}
                  onClick={() => setTag('')}
                >
                  All
                </button>
                {allTags.map(item => (
                  <button
                    key={item}
                    type="button"
                    className="pk-chip"
                    aria-pressed={tag === item}
                    onClick={() => setTag(tag === item ? '' : item)}
                  >
                    {item}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {content}
      </div>
    </main>
  );
};

export default GithubProjectsPage;
